/**
 * RPC Utility
 *
 * Purpose: Ensure all RPC calls target the correct schema (leltarmester_dev)
 *
 * Problem:
 *   - Dual schema architecture (leltarmester_dev vs leltarmester_prod)
 *   - Risk of calling production functions from dev environment
 *   - Inconsistent naming across codebase
 *
 * Solution:
 *   - Centralized RPC name resolution
 *   - Automatic schema suffix (_dev or _prod)
 *   - Type-safe function names
 *
 * Safety Rule:
 *   - During refactoring (Phase 0-2): ALWAYS append "_dev"
 *   - After production migration (Phase 3+): Use environment variable
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { logger } from '@/services/logger';

// ============================================================================
// Configuration
// ============================================================================

/**
 * Current schema target (from .env.local VITE_SUPABASE_SCHEMA)
 * leltarmester_prod → 'prod' (RPC suffix _prod)
 * leltarmester_dev or unset → 'dev' (RPC suffix _dev)
 */
const SCHEMA_TARGET: 'dev' | 'prod' =
  import.meta.env.VITE_SUPABASE_SCHEMA === 'leltarmester_prod' ? 'prod' : 'dev';

/**
 * Schema suffix mapping
 */
const SCHEMA_SUFFIXES = {
  dev: '_dev',
  prod: '_prod',
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * RPC function base names (without schema suffix)
 *
 * This is a registry of all RPC functions we use in the app.
 * Add new functions here as they are created.
 */
export const RPC_FUNCTIONS = {
  // Inventory Stats
  GET_INVENTORY_STATS: 'get_inventory_stats',
  GET_INVENTORY_PAGINATED: 'get_inventory_paginated',

  // Offline Sync (Phase 0, Sprint 0.3) - LEGACY, not used anymore
  SET_INVENTORY_COUNT_WITH_VERSION: 'set_inventory_count_with_version',
  ADJUST_INVENTORY_DELTA: 'adjust_inventory_delta',
  ADJUST_INVENTORY_BATCH: 'adjust_inventory_batch',

  // Performance Helpers (Phase 0, Sprint 0.3)
  GET_USER_COMPANY_ID: 'get_user_company_id',
  GET_CURRENT_USER_COMPANY_ID: 'get_current_user_company_id',

  // Transaction Processing (V2 - fixes COUNT type doubling issue)
  PROCESS_INVENTORY_TRANSACTION: 'process_inventory_transaction_v2',

  // Checkout Processing (POS mode)
  PROCESS_CHECKOUT_TRANSACTION: 'process_checkout_transaction',

  // POS Cart Processing (Phase 4 - Cleanup)
  PROCESS_POS_CART_TRANSACTION: 'process_pos_cart_transaction',

  // Stock Availability Check (Phase 4 - Cleanup)
  GET_AVAILABLE_STOCK: 'get_available_stock',

  // Add more as needed...
} as const;

/**
 * RPC function name type (for type safety)
 */
export type RpcFunctionName = (typeof RPC_FUNCTIONS)[keyof typeof RPC_FUNCTIONS];

// ============================================================================
// RPC Name Resolution
// ============================================================================

/**
 * Get the full RPC function name with correct schema suffix
 *
 * @param baseName Base name of the RPC function (without suffix)
 * @returns Full RPC function name with schema suffix
 *
 * @example
 * getRpcName('get_inventory_stats') // Returns: 'get_inventory_stats_dev'
 * getRpcName('adjust_inventory_delta') // Returns: 'adjust_inventory_delta_dev'
 */
export function getRpcName(baseName: string): string {
  // Get schema suffix
  const suffix = SCHEMA_SUFFIXES[SCHEMA_TARGET];

  // Check if already has suffix (avoid double-suffixing)
  if (baseName.endsWith(suffix)) {
    logger.warn(`RPC function already has suffix: ${baseName}`, undefined, 'RPC');
    return baseName;
  }

  // Append suffix
  const fullName = `${baseName}${suffix}`;

  logger.debug(`RPC name resolved: ${baseName} -> ${fullName}`, undefined, 'RPC');

  return fullName;
}

/**
 * Get RPC function name from registry (type-safe)
 *
 * @param key Key from RPC_FUNCTIONS registry
 * @returns Full RPC function name with schema suffix
 *
 * @example
 * getRpcNameFromRegistry('GET_INVENTORY_STATS') // Returns: 'get_inventory_stats_dev'
 * getRpcNameFromRegistry('ADJUST_INVENTORY_DELTA') // Returns: 'adjust_inventory_delta_dev'
 */
export function getRpcNameFromRegistry(key: keyof typeof RPC_FUNCTIONS): string {
  const baseName = RPC_FUNCTIONS[key];
  return getRpcName(baseName);
}

/**
 * Validate RPC function name
 *
 * Checks if the RPC function name has the correct schema suffix.
 * Useful for debugging and ensuring consistency.
 *
 * @param rpcName Full RPC function name
 * @returns True if valid, false otherwise
 */
export function validateRpcName(rpcName: string): boolean {
  const expectedSuffix = SCHEMA_SUFFIXES[SCHEMA_TARGET];
  const isValid = rpcName.endsWith(expectedSuffix);

  if (!isValid) {
    logger.error(
      `Invalid RPC function name: ${rpcName} (expected suffix: ${expectedSuffix})`,
      undefined,
      'RPC'
    );
  }

  return isValid;
}

// ============================================================================
// Schema Detection (Future-Proofing)
// ============================================================================

/**
 * Get current schema target
 *
 * @returns Current schema target ('dev' or 'prod')
 */
export function getSchemaTarget(): 'dev' | 'prod' {
  return SCHEMA_TARGET;
}

/**
 * Check if running in dev schema
 */
export function isDevSchema(): boolean {
  return SCHEMA_TARGET === 'dev';
}

/**
 * Check if running in prod schema
 */
export function isProdSchema(): boolean {
  return SCHEMA_TARGET === 'prod';
}

// ============================================================================
// Migration Helper (for Phase 3+)
// ============================================================================

/**
 * Switch schema target (for future production migration)
 *
 * WARNING: This is a placeholder for Phase 3+
 * Currently hardcoded to 'dev' for safety during refactoring
 *
 * @param target Target schema ('dev' or 'prod')
 */
export function setSchemaTarget(_target: 'dev' | 'prod'): void {
  // Disabled during refactoring (Phase 0-2)
  logger.warn(
    'Schema target switching is disabled during refactoring phase',
    { requestedTarget: _target, currentTarget: SCHEMA_TARGET },
    'RPC'
  );

  // TODO: In Phase 3+, uncomment this:
  // SCHEMA_TARGET = target;
  // logger.info(`Schema target switched to: ${target}`, undefined, 'RPC');
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get all RPC function names (for debugging)
 */
export function getAllRpcNames(): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, baseName] of Object.entries(RPC_FUNCTIONS)) {
    result[key] = getRpcName(baseName);
  }

  return result;
}

/**
 * Log all RPC function names (for debugging)
 */
export function logAllRpcNames(): void {
  const allNames = getAllRpcNames();
  logger.info('All RPC function names:', allNames, 'RPC');
}

// ============================================================================
// Example Usage (for documentation)
// ============================================================================

/*
Example usage in services:

// BEFORE (hardcoded):
const { data, error } = await supabase.rpc('get_inventory_stats_prod', { ... });

// AFTER (safe):
import { getRpcName, RPC_FUNCTIONS } from '@/utils/rpc';

const { data, error } = await supabase.rpc(
  getRpcName(RPC_FUNCTIONS.GET_INVENTORY_STATS),
  { ... }
);

// Alternative (more explicit):
import { getRpcNameFromRegistry } from '@/utils/rpc';

const { data, error } = await supabase.rpc(
  getRpcNameFromRegistry('GET_INVENTORY_STATS'),
  { ... }
);
*/
